import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Alert, Box, Checkbox, FormControlLabel, Link, MenuItem, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useRunPipeline from 'redux/slices/pipelines/hooks/useRunPipeline';
import useUpdatePipeline from 'redux/slices/pipelines/hooks/useUpdatePipeline';
import useGetPipelineBranches from 'redux/slices/pipelines/hooks/useGetPipelineBranches';
import { mapRunTriggerError } from 'redux/slices/pipelines/helpers/mapRunTriggerError';
import { buildBranchPickerOptions } from 'redux/slices/pipelines/helpers/buildBranchPickerOptions';
import { resolveBranchListUnavailableMessage } from 'redux/slices/pipelines/helpers/resolveBranchListUnavailableMessage';

const BranchControlMode = {
  Picker: 'picker',
  FreeText: 'freeText',
};

// Shared PAT-collecting Run step — used both by the wizard's step 4 (AC-8/AC-9) and the
// detail page's Run action (AC-18). The PAT (`AccessToken`) is NEVER given a default value,
// NEVER read from props/redux/storage, and is cleared from local state immediately after every
// RUN submit attempt, success or failure (AC-12/AC-25/NFR-Security) — it never leaves this
// component except inside the single POST /run request body (and, on-demand, the POST
// /branches request body — see handleListBranches).
//
// deploy-pipelines-run-overrides (Wave 4) adds the branch override + "save as default" affordances:
//   - AC-24: nothing is fetched when this component mounts (the dialog opens). The branch list is
//     populated ONLY via the explicit "Load branches from repository" button below, which is
//     itself disabled until a PAT has been entered.
//   - OQ-B: the fetched list lives in `branchListResult` (component state) ONLY — never redux,
//     never storage — fetched once per PAT entry, with no re-fetch affordance.
//   - AC-26: `freeTextBranch` is reachable at all times, both as the fallback when listing is
//     unavailable/fails/the repo type is unrecognised, AND as an explicit "enter manually instead"
//     escape hatch on the success path (a branch created after the list was fetched would otherwise
//     be permanently unreachable this dialog-open, OQ-B). It starts EMPTY and is *prefilled* only on
//     the unavailable arm, which is the only arm AC-26 asks for a prefill on; the `effectiveBranch`
//     prop (data the parent page ALREADY fetched via GET .../build-source — no new request) is shown
//     as the placeholder and in the helper text. See `branchTouched` for why that distinction matters.
//   - AC-21/AC-22/OQ-A: "save as default" is a checkbox that PATCHes `DefaultBranchName` on a
//     successful override run. It does not even render without PIPELINE_UPDATE (AC-22 —
//     unavailable, not merely ignored) and is disabled whenever no branch value is entered (there
//     is nothing to save).
const PipelineRunForm = ({ pipelineId, onTriggered, disabledReason, effectiveBranch }) => {
  const [accessToken, setAccessToken] = useState('');
  const [touched, setTouched] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Branch override state — component state only (OQ-B). `branchListResult` is the raw
  // PipelineBranchListDto from the last POST /branches response, or null before any fetch.
  const [branchMode, setBranchMode] = useState(BranchControlMode.FreeText);
  // H1 (code review): starts EMPTY, not prefilled from `effectiveBranch`. AC-26 scopes the prefill to
  // the listing-unavailable / unrecognised-repo arm only ("...then the user gets a free-text branch
  // field with the effective branch prefilled") — no AC asks for a prefilled value on the normal path,
  // and prefilling there made every run send an explicit override (see `branchTouched`). The effective
  // branch is still shown, as the field's placeholder and in the helper text below.
  const [freeTextBranch, setFreeTextBranch] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  // H1 (code review): "the user actually chose a branch", tracked explicitly rather than inferred from
  // the field being non-empty. Inferring it is what made a no-change Run send `BranchName`, which
  // forced an ADO `_apis/projects` probe that a Code(Read)-only PAT fails, mislabelled every 400 as a
  // branch problem, and left AC-1's no-override path unreachable outside the tests.
  //
  // NOT derived by comparing the value to `effectiveBranch`: a user who deliberately picks the branch
  // that is already effective AND checks "save as default" is doing something real — pinning the
  // legacy image-group value as the pipeline's OWN default, which flips `IsPipelineOwnedDefault`
  // (AC-9) — and that comparison would silently skip the PATCH.
  const [branchTouched, setBranchTouched] = useState(false);
  const [branchListResult, setBranchListResult] = useState(null);
  const [branchListError, setBranchListError] = useState(null);
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  const canRun = useAllowedAccess([AuthorizePermission.PIPELINE_RUN]);
  // AC-22 — the "save as default" checkbox is gated on the SAME permission the PATCH it issues
  // requires. Computed here (not left to a 403 at submit) so the control is UNAVAILABLE, not
  // merely disabled-and-ignored, for a user who cannot use it.
  const canSaveDefault = useAllowedAccess([AuthorizePermission.PIPELINE_UPDATE]);

  const runMutate = useRunPipeline();
  const updateMutate = useUpdatePipeline();
  const branchListMutate = useGetPipelineBranches();

  const isValid = accessToken.trim().length > 0;

  const { options: branchOptions, boundedNote } = useMemo(() => buildBranchPickerOptions(branchListResult), [branchListResult]);
  const unavailableMessage = useMemo(() => resolveBranchListUnavailableMessage(branchListResult), [branchListResult]);

  const currentBranchValue = branchMode === BranchControlMode.Picker ? selectedBranch : freeTextBranch;
  const trimmedBranchValue = (currentBranchValue ?? '').trim();

  // H1 — the single definition of "this run carries an override". A prefilled-but-untouched value (the
  // AC-26 fallback arm) is NOT an override; AC-19's "never sends ''" still holds via the trim.
  const branchOverride = branchTouched ? trimmedBranchValue || undefined : undefined;

  // OQ-B — "fetched once per PAT entry ... and no re-fetch button". Once a listing has come back
  // (available or not), the load affordance is spent for this dialog-open: the accepted consequence
  // is that a branch created afterwards will not appear until the dialog is reopened, and free text
  // (AC-26) is the deliberate escape hatch. A FAILED fetch does not spend it — nothing was fetched,
  // so retrying is not a RE-fetch.
  const hasFetchedBranchList = branchListResult != null;

  // AC-24 — this fires ONLY on the explicit click below, never on mount/open.
  const handleListBranches = async () => {
    if (!isValid || hasFetchedBranchList) return;

    setBranchListError(null);
    try {
      const { data } = await branchListMutate.mutateAsync({ id: pipelineId, AccessToken: accessToken });
      setBranchListResult(data);

      if (data?.Available && (data?.Branches ?? []).length > 0) {
        // AC-11/AC-12 — a usable list: switch to the picker, pre-selecting the effective branch
        // so the reader can see which one is the default (spec: "mark the effective branch").
        setBranchMode(BranchControlMode.Picker);
        setSelectedBranch(data.EffectiveBranch || freeTextBranch || '');
      } else {
        // AC-14/AC-26 — listing unavailable, or an available-but-empty list: stay on free text,
        // prefilled with the effective branch this response resolved (never blocks Run).
        setBranchMode(BranchControlMode.FreeText);
        setFreeTextBranch((prev) => data?.EffectiveBranch || prev);
      }
    } catch (error) {
      // AC-26 — a failed fetch (bad PAT, network error, unrecognised repo type) never blocks
      // Run; it falls back to free text, which is already the active/reachable control.
      setBranchListResult(null);
      setBranchMode(BranchControlMode.FreeText);
      const status = error?.response?.status;
      setBranchListError(
        status === 400
          ? "Could not list branches for this PAT — enter the branch name manually."
          : 'Could not load the branch list — enter the branch name manually.',
      );
    }
  };

  const handleRun = async () => {
    setTouched(true);
    if (!isValid) return;

    setErrorMessage(null);

    try {
      const { data } = await runMutate.mutateAsync({ id: pipelineId, AccessToken: accessToken, BranchName: branchOverride });

      // AC-21/OQ-A — "save as default" is a SEPARATE, explicit PATCH, issued only after the run
      // itself succeeded, and only when there is a branch value to save. A failure here does not
      // undo or block the already-triggered run — it is reported non-blockingly via toast.
      if (saveAsDefault && canSaveDefault && branchOverride) {
        try {
          await updateMutate.mutateAsync({ id: pipelineId, DefaultBranchName: branchOverride });
          toast.success(`Saved '${branchOverride}' as this pipeline's default branch.`);
        } catch {
          toast.error("Could not save this branch as the pipeline's default. The run itself was not affected.");
        }
      }

      onTriggered?.(data);
    } catch (error) {
      // AC-27/OQ-C — `!!branchOverride` tells mapRunTriggerError whether a 400 here is the
      // pre-existing "no build source" case or a rejected override branch/PAT (ValidateRepoAsync
      // only runs when an override was supplied).
      setErrorMessage(mapRunTriggerError(error, !!branchOverride));
    } finally {
      // Cleared on both the success and failure path — never retained (AC-12/AC-25).
      setAccessToken('');
    }
  };

  if (!canRun) {
    return (
      <Alert severity='info'>You do not have permission to run this pipeline.</Alert>
    );
  }

  if (disabledReason) {
    return <Alert severity='warning'>{disabledReason}</Alert>;
  }

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      <TextField
        type='password'
        label='Git Personal Access Token'
        required
        fullWidth
        autoComplete='off'
        value={accessToken}
        onChange={(e) => {
          setAccessToken(e.target.value);
          setErrorMessage(null);
        }}
        onBlur={() => setTouched(true)}
        error={touched && !isValid}
        helperText={
          touched && !isValid ? (
            'Git Personal Access Token is required'
          ) : (
            <>
              Used once to build your image, never stored.{' '}
              <Link
                href='https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens'
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn more
              </Link>
            </>
          )
        }
        inputProps={{ 'aria-label': 'Git Personal Access Token' }}
      />

      <Box display='flex' flexDirection='column' gap={1.5}>
        <Typography variant='subtitle2'>Branch (optional override)</Typography>

        {branchMode === BranchControlMode.Picker && branchOptions.length > 0 ? (
          <>
            <TextField
              select
              label='Branch'
              fullWidth
              value={selectedBranch}
              onChange={(e) => {
                // H1 — an explicit pick from the list IS a choice, even if it happens to be the
                // branch that was pre-selected as the current default.
                setSelectedBranch(e.target.value);
                setBranchTouched(true);
              }}
              helperText={boundedNote ?? "Leave as the current default, or pick a branch to override this run only."}
              inputProps={{ 'aria-label': 'Branch' }}
            >
              {branchOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Link component='button' type='button' variant='body2' onClick={() => setBranchMode(BranchControlMode.FreeText)}>
              Enter a branch name manually instead
            </Link>
          </>
        ) : (
          <>
            <TextField
              label='Branch'
              fullWidth
              placeholder={effectiveBranch || 'Uses the pipeline default'}
              value={freeTextBranch}
              onChange={(e) => {
                // H1 — typing in the field is a choice. Clearing it back to empty leaves
                // `branchOverride` undefined via the trim, so "no override" stays reachable.
                setFreeTextBranch(e.target.value);
                setBranchTouched(true);
              }}
              helperText={
                unavailableMessage ??
                (effectiveBranch
                  ? `Leave blank to build the current default (${effectiveBranch}), or type a branch to override this run only.`
                  : 'Leave blank to build the pipeline\'s current default, or type a branch to override this run only.')
              }
              inputProps={{ 'aria-label': 'Branch' }}
            />
            {branchOptions.length > 0 && (
              <Link component='button' type='button' variant='body2' onClick={() => setBranchMode(BranchControlMode.Picker)}>
                Choose from the branch list instead
              </Link>
            )}
          </>
        )}

        <Box>
          <LoadingButton
            size='small'
            loading={branchListMutate.isPending}
            disabled={!isValid || hasFetchedBranchList}
            onClick={handleListBranches}
          >
            Load branches from repository
          </LoadingButton>
        </Box>

        {branchListError && (
          <Alert severity='warning' role='alert'>
            {branchListError}
          </Alert>
        )}
      </Box>

      {canSaveDefault && (
        <FormControlLabel
          control={
            <Checkbox
              checked={saveAsDefault}
              // H1 — gated on the same signal that decides whether a run carries an override, so the
              // checkbox is not live on open just because the field shows a prefilled fallback value.
              disabled={!branchOverride}
              onChange={(e) => setSaveAsDefault(e.target.checked)}
              inputProps={{ 'aria-label': 'Save this branch as the pipeline default' }}
            />
          }
          label='Save this branch as the pipeline default'
        />
      )}

      {errorMessage && (
        <Alert severity='error' role='alert'>
          {errorMessage}
        </Alert>
      )}

      <Box>
        <LoadingButton
          variant='contained'
          loading={runMutate.isPending}
          disabled={!isValid}
          onClick={handleRun}
        >
          Run
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default React.memo(PipelineRunForm);

PipelineRunForm.propTypes = {
  pipelineId: PropTypes.string.isRequired,
  onTriggered: PropTypes.func,
  disabledReason: PropTypes.string,
  // deploy-pipelines-run-overrides (Wave 4, AC-26) — the pipeline's resolved next-run branch
  // (`PipelineBuildSourceDto.EffectiveBranchName`), threaded down from data the parent page
  // ALREADY fetched (no new request here). Prefills the free-text field and is shown in its
  // helper text. Optional: absent in the create wizard's Run step, where no build source can
  // exist yet for a just-created pipeline.
  effectiveBranch: PropTypes.string,
};
